import { Suspense, useState, useRef, useEffect } from "react";
import styled from "styled-components";
import {
  Navigate,
  Route,
  Routes as RoutesReactRouterDom,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { logOut } from "../app/loginReducer";
import { LogoutRequestBody } from "../app/models/auth/loginUser";
import { logout } from "../http/authentication-services";

import { GenericNotFound, Login } from "./paths";
import Menu from "../components/Menu/Menu";
import { RouteConstants } from "../constants/RouteConstants";
import { useTranslation } from "react-i18next";

import { AppConstants } from "../constants/AppConstants";
import Modal from "../components/shared/ModalPortal/Modal";
import BiButton from "../components/primitives/buttons/BiButton.primitive";
import { fetchFromCookie, getUserRoles } from "../util/admin-utils";

import RequestDetails from "../components/PatientRequest/RequestDetails";
import PastRequests from "../components/PatientRequest/PastRequests";
import Customization from "../components/Customization/Customization";
import { useCheckMobileScreen } from "../util/customHooks";
import { returnErrorPage } from "../util/appInitializeUtil";
import MenuTop from "../components/Menu/MenuTop";
import AppInitialize from "./AppInitialize";

export const Routes = () => {
  const { search } = useLocation();
  let warningTime: any = useRef(null);
  let logoutTime: any = useRef(null);
  let remainingTime: any = useRef(null);
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const [showAccountLockout, setShowAccountLockout] = useState(false);
  const [showInactivityDialog, setShowInactivityDialog] = useState(false);
  const [timerText, setTimerText] = useState("");
  const loggedIn = useAppSelector((state) => state.login.isLoggedIn);
  const [errorFlag, setErrorFlag] = useState(false);
  const userProfile = useAppSelector(
    (state) => state?.userManagement?.userProfileInfo
  );

  const roleName = getUserRoles(userProfile)
    .map((role: any) => role?.name)
    .join();

  const { t } = useTranslation();

  document.onmousemove = resetTimer;
  document.onkeydown = resetTimer;

  function resetTimer() {
    const { session = {} } = {} as any;
    const {
      inactivityLogout:
        inactivityLogoutTime = AppConstants.DEFAULT_INACTIVITY_LOGOUT_TIME,
      inactivityWarning = AppConstants.DEFAULT_INACTIVITY_WARNING_TIME,
    } = session;

    if (loggedIn && !showInactivityDialog) {
      clearTimeout(warningTime.current);
      clearTimeout(logoutTime.current);
      warningTime.current = setTimeout(
        () => warnInactivity(),
        inactivityWarning
      );
      logoutTime.current = setTimeout(
        () => inactivityLogout(),
        inactivityLogoutTime
      );
    }
  }

  async function inactivityLogout() {
    clearInterval(remainingTime.current);
    setShowInactivityDialog(false);
    const logoutBody: LogoutRequestBody = {
      client_id: "oidc_pkce",
      token: fetchFromCookie("access-token") || "", // localStorage.getItem('access-token') || ''
    };
    await logout(logoutBody);
    dispatch(logOut());
    return `${t("T_LOGOUT_MSG")}`;
  }

  function warnInactivity() {
    const { session = {} } = {} as any;
    let {
      inactivityLogout = AppConstants.DEFAULT_INACTIVITY_LOGOUT_TIME,
      inactivityWarning = AppConstants.DEFAULT_INACTIVITY_WARNING_TIME,
    } = session;

    let remainingTimeText = "";
    clearInterval(remainingTime.current);
    remainingTime.current = setInterval(() => {
      const timeleft = inactivityLogout - inactivityWarning;
      let minutes, seconds;
      minutes = Math.floor((timeleft % (1000 * 60 * 60)) / (1000 * 60));
      seconds = Math.floor((timeleft % (1000 * 60)) / 1000);
      if (timeleft < 0) {
        clearInterval(remainingTime.current);
      }
      inactivityWarning = inactivityWarning + 1000;
      remainingTimeText = `${minutes} minutes and ${seconds} seconds.`;
      setTimerText(remainingTimeText);
      setShowInactivityDialog(true);
    }, 1000);

    return remainingTimeText;
  }

  useEffect(() => {
    setErrorFlag(returnErrorPage());
  }, []);

  useEffect(() => {
    if (errorFlag) {
      navigate(RouteConstants.ROUTE_NOT_FOUND);
    }
  }, [errorFlag]);
  return (
    <Suspense fallback={<p>Loading</p>}>
      {loggedIn && showInactivityDialog && (
        <Modal
          overflowVisible={true}
          showCloseIcon={true}
          width="30rem"
          showHeader={true}
          headerText={t("T_SESSION_TIMEOUT_HEADER")}
          toggleVisiblity={(value: boolean) => {
            clearInterval(remainingTime.current);
            setShowInactivityDialog(false);
          }}
          isVisible={showInactivityDialog}
          body={
            <>
              <div className="mb-5 flex flex-col">
                <div className="">{`${t(
                  "T_SESSION_TIMEOUT_BODY_TEXT"
                )} ${timerText}`}</div>
              </div>

              <ModalButton>
                <BiButton
                  className={"mr-2 text-primary"}
                  type="button"
                  onClick={() => {
                    clearInterval(remainingTime.current);
                    setShowInactivityDialog(false);
                  }}
                >
                  {t("T_SESSION_STAY_SIGNED_IN")}
                </BiButton>
                <BiButton
                  className={"bg-primary text-white"}
                  type="button"
                  onClick={() => {
                    inactivityLogout();
                  }}
                  // disabled={!permissions.create}
                >
                  {t("T_SESSION_SIGN_OUT")}
                </BiButton>
              </ModalButton>
            </>
          }
        />
      )}
      {loggedIn && (
        <Menu modalIsActive={false}>
          <AppInitialize>
            <RoutesReactRouterDom>
              <Route
                path={RouteConstants.ROUTE_NOT_FOUND}
                element={<GenericNotFound />}
              />
              <Route
                path="*"
                element={
                  <Navigate
                    to={loggedIn ? RouteConstants.ROUTE_GDPR_CREATE : "auth"}
                  />
                }
              />

              <Route
                path={RouteConstants.ROUTE_GDPR_CREATE}
                element={<RequestDetails />}
              ></Route>
              <Route
                path={RouteConstants.ROUTE_GDPR_LIST}
                element={<PastRequests />}
              ></Route>
              <Route
                path={RouteConstants.ROUTE_GDPR_VIEW}
                element={<RequestDetails />}
              ></Route>

              {!AppConstants.DISABLED_ROLES_CUSTOMIZATION.includes(roleName) ? (
                <Route
                  path={RouteConstants.ROUTE_CUSTOMIZATION}
                  element={<Customization />}
                ></Route>
              ) : (
                ""
              )}
              <Route
                path={RouteConstants.ROUTE_NOT_FOUND}
                element={<GenericNotFound />}
              />
            </RoutesReactRouterDom>
          </AppInitialize>
        </Menu>
      )}
      {!loggedIn && errorFlag && (
        <>
          <MenuTop loggedIn={loggedIn} />
          <RoutesReactRouterDom>
            <Route
              path={RouteConstants.ROUTE_NOT_FOUND}
              element={<GenericNotFound />}
            />
          </RoutesReactRouterDom>
        </>
      )}
      {!loggedIn && !errorFlag && (
        <RoutesReactRouterDom>
          <Route path="" element={<Login />} />
          <Route
            path={RouteConstants.ROUTE_NOT_FOUND}
            element={<GenericNotFound />}
          />
          <Route
            path="*"
            element={
              <Navigate to={loggedIn ? RouteConstants.ROUTE_GDPR_CREATE : ""} />
            }
          />
        </RoutesReactRouterDom>
      )}
    </Suspense>
  );
};

export const ModalButton = styled("div")`
  display: flex;
  justify-content: end;
`;
